import { Directive, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { Subscription } from 'rxjs';

import { AccountVersionService } from '../services/account-version.service';

/**
 * Aplica no elemento hospedeiro a classe de tema da versão corrente
 * (`version-national` / `version-international`), que é o que faz os tokens
 * `--sms-*` de `src/theme.scss` valerem para dentro.
 *
 * Existe por causa das telas de fora do painel (CU-86akkmb97). O
 * `LayoutComponent` marca a versão na raiz do shell, mas login, cadastro e
 * recuperação de senha não são filhas dele: sem esta diretiva elas ficariam
 * presas no valor nacional de `:root`, e o login apareceria verde para quem vai
 * cair num painel azul.
 *
 * Antes do login não existe usuário, então a versão vem da chave
 * `SMSFUNNEL::GLOBAL_ACCOUNT` — o espelho que o `AuthService` grava a partir do
 * usuário efetivo (`syncGlobalAccountKey`) e o `AccountVersionService` publica.
 *
 * Na prática essa chave quase nunca chega viva até aqui, e o docblock anterior
 * prometia mais do que existe. `AuthService.removeData()` é `localStorage.clear()`,
 * e os dois jeitos de uma sessão acabar passam por ele: o logout explícito e a
 * falha do refresh. Nada grava a chave sem sessão. Sobra o caso em que ninguém
 * encerrou nada — aba fechada, ou navegação para fora do painel — e mesmo esse
 * raramente termina numa destas telas, porque quem ainda tem sessão vai para o
 * shell. Ou seja: aqui é nacional em quase todo caso, e está certo assim —
 * nacional é o padrão de quem chega sem sessão. A diretiva existe para aplicar o
 * tema, não para prometer continuidade de realm entre sessões.
 *
 * A assinatura é do stream e não uma leitura única: a diretiva não sabe em que
 * tela está, e no shell a versão troca com a tela aberta.
 */
@Directive({
  selector: '[appAccountVersionTheme]'
})
export class AccountVersionThemeDirective implements OnInit, OnDestroy {

  private subscription: Subscription;
  private appliedClass: string = null;

  constructor(
    private element: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    private accountVersionService: AccountVersionService
  ) { }

  ngOnInit(): void {
    this.subscription = this.accountVersionService
      .getVersion()
      .subscribe(() => this.applyThemeClass(this.accountVersionService.themeClass));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  private applyThemeClass(themeClass: string): void {
    if (this.appliedClass === themeClass) {
      return;
    }

    if (this.appliedClass) {
      this.renderer.removeClass(this.element.nativeElement, this.appliedClass);
    }

    this.renderer.addClass(this.element.nativeElement, themeClass);
    this.appliedClass = themeClass;
  }
}
